/**
 * SurveySettings Page
 *
 * Allows survey owners to manage survey scheduling (start/end dates),
 * status, and other settings.
 */

import React, { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuth } from './auth-context'
import * as surveysApi from './api/surveys'
import { uploadSurveyBrandImage } from './api/media'
import './App.css'

function SurveySettings() {
  const { id } = useParams()
  const { user, loading: authLoading, currentOrg, userRole } = useAuth()

  const [survey, setSurvey] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Field agent toggle
  const [requiresFieldAgent, setRequiresFieldAgent] = useState(false)

  // Activation status toggle
  const [isActive, setIsActive] = useState(false)

  // Check if user is the organization owner
  const isOwner = userRole === 'owner'

  // Brand image
  const [brandImageUrl, setBrandImageUrl] = useState('')
  const [brandUploading, setBrandUploading] = useState(false)
  const brandInputRef = useRef(null)

  // Schedule form state
  const [scheduleForm, setScheduleForm] = useState({
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
  })

  // Load survey data
  useEffect(() => {
    if (authLoading || !user || !id) return

    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        const data = await surveysApi.getSurvey(id)
        if (cancelled) return
        setSurvey(data)

        // Initialize field agent setting
        setRequiresFieldAgent(data.requires_field_agent || false)

        // Initialize activation status
        setIsActive(data.is_active || false)

        // Initialize brand image
        setBrandImageUrl(data.theme?.brand_image_url || '')

        // Initialize form with existing dates
        if (data.scheduled_publish_at) {
          const start = new Date(data.scheduled_publish_at)
          setScheduleForm((prev) => ({
            ...prev,
            startDate: start.toISOString().split('T')[0],
            startTime: start.toTimeString().slice(0, 5),
          }))
        }
        if (data.scheduled_close_at) {
          const end = new Date(data.scheduled_close_at)
          setScheduleForm((prev) => ({
            ...prev,
            endDate: end.toISOString().split('T')[0],
            endTime: end.toTimeString().slice(0, 5),
          }))
        }
      } catch (e) {
        if (!cancelled) {
          setError('Failed to load survey')
          console.error('Failed to load survey:', e)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [id, user, authLoading])

  // Get survey status display
  const getSurveyStatus = () => {
    if (!survey) return { status: 'unknown', label: 'Unknown', color: '#6c757d' }

    const now = new Date()
    const startDate = survey.scheduled_publish_at ? new Date(survey.scheduled_publish_at) : null
    const endDate = survey.scheduled_close_at ? new Date(survey.scheduled_close_at) : null

    if (survey.status === 'draft') {
      return { status: 'draft', label: 'Draft', color: '#ffc107' }
    }
    if (survey.status === 'archived') {
      return { status: 'archived', label: 'Archived', color: '#6c757d' }
    }

    // Published status
    if (survey.status === 'published') {
      // Check if survey is within schedule
      const isWithinSchedule = startDate && endDate && now >= startDate && now <= endDate

      // Survey is active if: is_active=true OR within scheduled window
      const isCurrentlyActive = survey.is_active || isWithinSchedule

      if (!survey.is_active && startDate && now < startDate) {
        return { status: 'scheduled', label: 'Scheduled', color: '#17a2b8' }
      }
      if (endDate && now > endDate && !survey.is_active) {
        return { status: 'ended', label: 'Ended', color: '#dc3545' }
      }
      if (isCurrentlyActive) {
        return { status: 'active', label: 'Active', color: '#28a745' }
      }
      // Published but not active
      return { status: 'inactive', label: 'Inactive', color: '#6c757d' }
    }

    return { status: survey.status, label: survey.status, color: '#6c757d' }
  }

  // Handle schedule save
  const handleSaveSchedule = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (!scheduleForm.startDate || !scheduleForm.endDate) {
      setError('Please set both start and end dates')
      return
    }

    const startDateTime = new Date(
      `${scheduleForm.startDate}T${scheduleForm.startTime || '00:00'}:00`
    )
    const endDateTime = new Date(`${scheduleForm.endDate}T${scheduleForm.endTime || '23:59'}:00`)

    if (endDateTime <= startDateTime) {
      setError('End date must be after start date')
      return
    }

    setSaving(true)
    try {
      const updated = await surveysApi.scheduleSurvey(id, {
        start_date: startDateTime.toISOString(),
        end_date: endDateTime.toISOString(),
      })
      setSurvey(updated)
      setSuccess('Schedule saved successfully!')
    } catch (e) {
      console.error('Failed to save schedule:', e)
      setError(e.message || 'Failed to save schedule')
    } finally {
      setSaving(false)
    }
  }

  // Handle publish (schedule is optional)
  const handlePublish = async ({ andActivate = false } = {}) => {
    setError('')
    setSuccess('')

    setSaving(true)
    try {
      // Save schedule if dates are provided (optional)
      if (scheduleForm.startDate && scheduleForm.endDate) {
        const startDateTime = new Date(
          `${scheduleForm.startDate}T${scheduleForm.startTime || '00:00'}:00`
        )
        const endDateTime = new Date(
          `${scheduleForm.endDate}T${scheduleForm.endTime || '23:59'}:00`
        )

        if (endDateTime <= startDateTime) {
          setError('End date must be after start date')
          setSaving(false)
          return
        }

        await surveysApi.scheduleSurvey(id, {
          start_date: startDateTime.toISOString(),
          end_date: endDateTime.toISOString(),
        })
      }

      // Publish the survey
      let updated = await surveysApi.publishSurvey(id)

      // Optionally activate immediately
      if (andActivate) {
        updated = await surveysApi.updateSurvey(id, { is_active: true })
        setIsActive(true)
      }

      setSurvey(updated)
      setSuccess(
        andActivate
          ? "Survey published and activated! It's now accepting responses."
          : 'Survey published successfully!'
      )
    } catch (e) {
      console.error('Failed to publish:', e)
      setError(e.message || 'Failed to publish survey')
    } finally {
      setSaving(false)
    }
  }

  // Handle archive
  const handleArchive = async () => {
    if (
      !confirm('Are you sure you want to archive this survey? It will stop accepting responses.')
    ) {
      return
    }

    setSaving(true)
    try {
      const updated = await surveysApi.archiveSurvey(id)
      setSurvey(updated)
      setSuccess('Survey archived successfully!')
    } catch (e) {
      console.error('Failed to archive:', e)
      setError(e.message || 'Failed to archive survey')
    } finally {
      setSaving(false)
    }
  }

  // Handle field agent toggle
  const handleFieldAgentToggle = async (checked) => {
    setError('')
    setSuccess('')
    setRequiresFieldAgent(checked)

    setSaving(true)
    try {
      const updated = await surveysApi.updateSurvey(id, {
        requires_field_agent: checked,
      })
      setSurvey(updated)
      setSuccess(
        checked
          ? 'Survey now requires field agent links for access'
          : 'Survey can be accessed without field agent links'
      )
    } catch (e) {
      console.error('Failed to update field agent setting:', e)
      setError(e.message || 'Failed to update setting')
      setRequiresFieldAgent(!checked) // Revert on error
    } finally {
      setSaving(false)
    }
  }

  // Handle activation toggle (owner only)
  const handleActivationToggle = async (checked) => {
    setError('')
    setSuccess('')
    setIsActive(checked)

    setSaving(true)
    try {
      const updated = await surveysApi.updateSurvey(id, {
        is_active: checked,
      })
      setSurvey(updated)
      setSuccess(
        checked
          ? 'Survey is now active and accepting responses'
          : 'Survey is now inactive and will not accept responses (unless within schedule)'
      )
    } catch (e) {
      console.error('Failed to update activation status:', e)
      setError(e.message || 'Failed to update activation status')
      setIsActive(!checked) // Revert on error
    } finally {
      setSaving(false)
    }
  }

  // Handle brand image file upload
  const handleBrandFileUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPEG, PNG, WebP, or GIF)')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Brand image must be under 5MB')
      return
    }

    try {
      setBrandUploading(true)
      setError('')
      const result = await uploadSurveyBrandImage(id, file)
      setBrandImageUrl(result.url)
      setSuccess('Brand image uploaded successfully!')
      // Refresh survey data
      const data = await surveysApi.getSurvey(id)
      setSurvey(data)
    } catch (err) {
      console.error('Brand upload failed:', err)
      setError(err.message || 'Failed to upload brand image')
    } finally {
      setBrandUploading(false)
      if (brandInputRef.current) brandInputRef.current.value = ''
    }
  }

  const handleSaveBrandUrl = async () => {
    setError('')
    setSuccess('')
    setSaving(true)
    try {
      const updated = await surveysApi.updateSurvey(id, {
        theme: { brand_image_url: brandImageUrl || null },
      })
      setSurvey(updated)
      setSuccess('Brand image URL saved!')
    } catch (e) {
      console.error('Failed to save brand URL:', e)
      setError(e.message || 'Failed to save brand image URL')
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveBrand = async () => {
    setError('')
    setSuccess('')
    setSaving(true)
    try {
      const updated = await surveysApi.updateSurvey(id, {
        theme: { brand_image_url: null },
      })
      setSurvey(updated)
      setBrandImageUrl('')
      setSuccess('Brand image removed!')
    } catch (e) {
      setError(e.message || 'Failed to remove brand image')
    } finally {
      setSaving(false)
    }
  }

  // Handle linking survey to organization
  const handleLinkToOrganization = async () => {
    if (!currentOrg) {
      setError('No organization selected')
      return
    }

    setError('')
    setSuccess('')
    setSaving(true)

    try {
      const updated = await surveysApi.updateSurvey(id, {
        organization_id: currentOrg.id,
      })
      setSurvey(updated)
      setSuccess(`Survey linked to ${currentOrg.name}`)
    } catch (e) {
      console.error('Failed to link survey:', e)
      setError(e.message || 'Failed to link survey to organization')
    } finally {
      setSaving(false)
    }
  }

  const statusInfo = getSurveyStatus()

  if (authLoading || loading) {
    return (
      <div className="survey-settings-page">
        <div className="container">
          <div className="loading-state">
            <div className="loading-spinner">
              <div className="spinner-ring"></div>
            </div>
            <p>Loading survey settings...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="survey-settings-page">
        <div className="container">
          <div className="error-state">
            <p>Please log in to manage survey settings.</p>
            <Link to="/login">Login</Link>
          </div>
        </div>
      </div>
    )
  }

  if (!survey) {
    return (
      <div className="survey-settings-page">
        <div className="container">
          <div className="error-state">
            <p>Survey not found.</p>
            <Link to="/dashboard">Back to Dashboard</Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="survey-settings-page">
      <div className="container" style={{ maxWidth: '800px', padding: '2rem' }}>
        {/* Header */}
        <div className="settings-header" style={{ marginBottom: '2rem' }}>
          <Link to="/dashboard" style={{ color: '#0D9488', textDecoration: 'none' }}>
            ← Back to Dashboard
          </Link>
          <h1 style={{ margin: '1rem 0 0.5rem 0' }}>Survey Settings</h1>
          <h2 style={{ margin: 0, fontWeight: 'normal', color: '#666' }}>{survey.title}</h2>
        </div>

        {/* Status Badge */}
        <div
          className="status-section"
          style={{
            background: '#f8f9fa',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem',
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '20px',
                background: statusInfo.color,
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.9rem',
              }}>
              {statusInfo.label}
            </span>
            <span style={{ color: '#666' }}>
              {survey.responses_count} response{survey.responses_count !== 1 ? 's' : ''} collected
            </span>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div
            style={{
              background: '#f8d7da',
              color: '#721c24',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1rem',
            }}>
            {error}
          </div>
        )}
        {success && (
          <div
            style={{
              background: '#d4edda',
              color: '#155724',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1rem',
            }}>
            {success}
          </div>
        )}

        {/* Organization Section */}
        {!survey.organization_id && currentOrg && (
          <div
            className="organization-section"
            style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '2rem',
            }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#856404' }}>
              ⚠️ Survey Not Linked to Organization
            </h3>
            <p style={{ color: '#856404', marginBottom: '1rem' }}>
              This survey is not linked to any organization. To assign field agents and use advanced
              features, you need to link it to your organization.
            </p>
            <button
              onClick={handleLinkToOrganization}
              disabled={saving}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#0D9488',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                opacity: saving ? 0.6 : 1,
              }}>
              {saving ? 'Linking...' : `Link to ${currentOrg.name}`}
            </button>
          </div>
        )}

        {survey.organization_id && (
          <div
            className="organization-section"
            style={{
              background: '#d4edda',
              border: '1px solid #28a745',
              borderRadius: '12px',
              padding: '1rem 1.5rem',
              marginBottom: '2rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}>
            <span style={{ color: '#155724' }}>✓ Linked to organization</span>
          </div>
        )}

        {/* Branding Section */}
        <div
          className="branding-section"
          style={{
            background: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem',
          }}>
          <h3 style={{ margin: '0 0 0.5rem 0' }}>Survey Branding</h3>
          <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
            Set a brand/logo image for this survey. This overrides the organization logo. If not
            set, the organization logo will be used as default.
          </p>

          {brandImageUrl && (
            <div
              style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <img
                src={brandImageUrl}
                alt="Survey Brand"
                style={{
                  height: 60,
                  maxWidth: 200,
                  objectFit: 'contain',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6',
                  padding: '4px',
                }}
                onError={(e) => {
                  e.target.style.display = 'none'
                }}
              />
              <button
                onClick={handleRemoveBrand}
                disabled={saving}
                style={{
                  padding: '0.25rem 0.75rem',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                }}>
                Remove
              </button>
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <input
              type="url"
              value={brandImageUrl}
              onChange={(e) => setBrandImageUrl(e.target.value)}
              placeholder="https://example.com/brand-logo.png"
              style={{
                flex: 1,
                padding: '0.75rem',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                fontSize: '0.95rem',
              }}
            />
            <button
              onClick={handleSaveBrandUrl}
              disabled={saving}
              style={{
                padding: '0.75rem 1rem',
                background: '#0D9488',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                whiteSpace: 'nowrap',
              }}>
              Save URL
            </button>
          </div>

          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.75rem' }}>
            <span style={{ color: '#6c757d', fontSize: '0.85rem' }}>or upload an image:</span>
            <input
              ref={brandInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleBrandFileUpload}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => brandInputRef.current?.click()}
              disabled={brandUploading}
              style={{
                padding: '0.5rem 1rem',
                background: '#e2e8f0',
                color: '#333',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}>
              {brandUploading ? 'Uploading...' : 'Choose Image'}
            </button>
            <span style={{ color: '#999', fontSize: '0.75rem' }}>
              Max 5 MB · JPEG, PNG, WebP, or GIF
            </span>
          </div>

          {survey?.organization_logo_url && !brandImageUrl && (
            <div
              style={{
                marginTop: '0.75rem',
                padding: '1rem',
                background: '#f8f9fa',
                borderRadius: '8px',
              }}>
              <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
                Currently using the organization logo (shown below). Upload or paste a URL above to
                override it for this survey.
              </div>
              <img
                src={survey.organization_logo_url}
                alt="Organization Logo"
                style={{
                  height: 48,
                  maxWidth: 180,
                  objectFit: 'contain',
                  borderRadius: '6px',
                  border: '1px solid #dee2e6',
                  padding: '4px',
                  background: 'white',
                }}
                onError={(e) => {
                  e.target.style.display = 'none'
                }}
              />
            </div>
          )}
        </div>

        {/* Field Agent Settings */}
        <div
          className="field-agent-section"
          style={{
            background: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem',
          }}>
          <h3 style={{ margin: '0 0 1rem 0' }}>Field Agent Settings</h3>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                cursor: saving ? 'not-allowed' : 'pointer',
              }}>
              <input
                type="checkbox"
                checked={requiresFieldAgent}
                onChange={(e) => handleFieldAgentToggle(e.target.checked)}
                disabled={saving}
                style={{
                  width: '20px',
                  height: '20px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              />
              <span style={{ fontWeight: '600' }}>Require Field Agent Links</span>
            </label>
          </div>
          <p style={{ color: '#666', marginTop: '0.5rem', fontSize: '0.9rem' }}>
            {requiresFieldAgent
              ? '✓ Survey can only be accessed via assigned field agent links. Direct sharing links will not work.'
              : 'Survey can be accessed by anyone with the direct link.'}
          </p>
          {requiresFieldAgent && (
            <div
              style={{
                marginTop: '1rem',
                padding: '0.75rem',
                background: '#e7f3ff',
                borderRadius: '8px',
                fontSize: '0.9rem',
              }}>
              <strong>Note:</strong> When assigning field agents to this survey, you must provide
              their GPS coordinates and location name for geofencing purposes.
            </div>
          )}
        </div>

        {/* Activation Section - Only visible to owner */}
        {survey.status === 'published' && (
          <div
            className="activation-section"
            style={{
              background: 'white',
              border: '1px solid #dee2e6',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '2rem',
              position: 'relative',
            }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>Survey Activation</h3>
            {!isOwner && (
              <div
                style={{
                  position: 'absolute',
                  top: '1.5rem',
                  right: '1.5rem',
                  padding: '0.5rem 1rem',
                  background: '#fff3cd',
                  color: '#856404',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                }}>
                Only the organization owner can activate surveys
              </div>
            )}
            <p style={{ color: '#666', marginBottom: '1rem' }}>
              Surveys are inactive by default. You can activate the survey immediately or set a
              schedule.
            </p>

            <div
              style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: !isOwner || saving ? 'not-allowed' : 'pointer',
                }}>
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => handleActivationToggle(e.target.checked)}
                  disabled={!isOwner || saving}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: !isOwner || saving ? 'not-allowed' : 'pointer',
                  }}
                />
                <span style={{ fontWeight: '600' }}>Activate Survey Immediately</span>
              </label>

              <span
                style={{
                  padding: '0.25rem 0.75rem',
                  borderRadius: '9999px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  background: isActive ? '#d4edda' : '#f8d7da',
                  color: isActive ? '#155724' : '#721c24',
                }}>
                {isActive ? 'Active' : 'Inactive'}
              </span>
            </div>

            <div
              style={{
                padding: '0.75rem',
                background: '#e7f3ff',
                borderRadius: '8px',
                fontSize: '0.9rem',
              }}>
              <strong>Note:</strong> Survey will accept responses if:
              <ul style={{ margin: '0.5rem 0 0 1.5rem', padding: 0 }}>
                <li>Survey is activated manually (toggle above), OR</li>
                <li>Current time is within the scheduled time window (set below)</li>
              </ul>
            </div>
          </div>
        )}

        {/* Schedule Section */}
        <div
          className="schedule-section"
          style={{
            background: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem',
            position: 'relative',
          }}>
          <h3 style={{ margin: '0 0 1rem 0' }}>Survey Schedule</h3>
          {!isOwner && (
            <div
              style={{
                position: 'absolute',
                top: '1.5rem',
                right: '1.5rem',
                padding: '0.5rem 1rem',
                background: '#fff3cd',
                color: '#856404',
                borderRadius: '6px',
                fontSize: '0.85rem',
              }}>
              Only the organization owner can modify the schedule
            </div>
          )}
          <p style={{ color: '#666', marginBottom: '1.5rem' }}>
            Optionally set when your survey will start and stop accepting responses. You can also
            activate the survey manually without setting a schedule.
          </p>

          <form onSubmit={handleSaveSchedule}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              {/* Start Date */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Start Date *
                </label>
                <input
                  type="date"
                  value={scheduleForm.startDate}
                  onChange={(e) =>
                    setScheduleForm((prev) => ({ ...prev, startDate: e.target.value }))
                  }
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    backgroundColor: !isOwner ? '#f5f5f5' : 'white',
                    cursor: !isOwner ? 'not-allowed' : 'text',
                  }}
                  required
                  disabled={!isOwner}
                />
              </div>

              {/* Start Time */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Start Time
                </label>
                <input
                  type="time"
                  value={scheduleForm.startTime}
                  onChange={(e) =>
                    setScheduleForm((prev) => ({ ...prev, startTime: e.target.value }))
                  }
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    backgroundColor: !isOwner ? '#f5f5f5' : 'white',
                    cursor: !isOwner ? 'not-allowed' : 'text',
                  }}
                  disabled={!isOwner}
                />
              </div>

              {/* End Date */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  End Date *
                </label>
                <input
                  type="date"
                  value={scheduleForm.endDate}
                  onChange={(e) =>
                    setScheduleForm((prev) => ({ ...prev, endDate: e.target.value }))
                  }
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    backgroundColor: !isOwner ? '#f5f5f5' : 'white',
                    cursor: !isOwner ? 'not-allowed' : 'text',
                  }}
                  required
                  disabled={!isOwner}
                />
              </div>

              {/* End Time */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  End Time
                </label>
                <input
                  type="time"
                  value={scheduleForm.endTime}
                  onChange={(e) =>
                    setScheduleForm((prev) => ({ ...prev, endTime: e.target.value }))
                  }
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    backgroundColor: !isOwner ? '#f5f5f5' : 'white',
                    cursor: !isOwner ? 'not-allowed' : 'text',
                  }}
                  disabled={!isOwner}
                />
              </div>
            </div>

            {isOwner && (
              <button
                type="submit"
                disabled={saving}
                style={{
                  marginTop: '1.5rem',
                  padding: '0.75rem 1.5rem',
                  background: '#0D9488',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                  fontSize: '1rem',
                  fontWeight: '600',
                }}>
                {saving ? 'Saving...' : 'Save Schedule'}
              </button>
            )}
          </form>
        </div>

        {/* Actions Section */}
        <div
          className="actions-section"
          style={{
            background: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '12px',
            padding: '1.5rem',
          }}>
          <h3 style={{ margin: '0 0 1rem 0' }}>Survey Actions</h3>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <Link
              to={`/survey/${id}/edit`}
              style={{
                display: 'inline-block',
                padding: '0.75rem 1.5rem',
                background: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: '1rem',
                fontWeight: '600',
              }}>
              Edit Survey
            </Link>

            {survey.status === 'draft' && (
              <>
                <button
                  onClick={() => handlePublish()}
                  disabled={saving}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    opacity: saving ? 0.7 : 1,
                    fontSize: '1rem',
                    fontWeight: '600',
                  }}>
                  Publish Survey
                </button>
                {isOwner && (
                  <button
                    onClick={() => handlePublish({ andActivate: true })}
                    disabled={saving}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: 'linear-gradient(135deg, #28a745, #20c997)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      opacity: saving ? 0.7 : 1,
                      fontSize: '1rem',
                      fontWeight: '600',
                    }}>
                    Publish & Activate Now
                  </button>
                )}
              </>
            )}

            {survey.status === 'published' && (
              <button
                onClick={handleArchive}
                disabled={saving}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                  fontSize: '1rem',
                  fontWeight: '600',
                }}>
                Archive Survey
              </button>
            )}

            <Link
              to={`/analytics/${id}`}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#0D9488',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: '1rem',
                fontWeight: '600',
              }}>
              View Analytics
            </Link>

            <Link
              to={`/survey/${id}/field-agents`}
              style={{
                display: 'inline-block',
                padding: '0.75rem 1.5rem',
                background: '#20c997',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: '1rem',
                fontWeight: '600',
              }}>
              Manage Field Agents
            </Link>

            {survey.status === 'published' && (
              <button
                onClick={() => {
                  const url = survey.short_url || `${window.location.origin}/run/${survey.id}`
                  const message = `📋 Please take a moment to complete this survey:\n\n${survey.title || 'Survey'}\n\n${url}`
                  navigator.clipboard.writeText(message)
                  setSuccess('Survey link copied to clipboard!')
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'white',
                  color: '#0D9488',
                  border: '2px solid #0D9488',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                }}>
                Copy Share Link
              </button>
            )}
          </div>
        </div>

        {/* Share URL Section - only for published surveys */}
        {survey.status === 'published' && (
          <div
            className="share-url-section"
            style={{
              background: 'white',
              border: '1px solid #dee2e6',
              borderRadius: '12px',
              padding: '1.5rem',
              marginTop: '2rem',
            }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>Share URL</h3>

            {survey.short_url ? (
              <div>
                <p style={{ color: '#666', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
                  Use this shortened URL to share the survey. It's easier to remember and share.
                </p>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem 1rem',
                    background: '#f0f7ff',
                    border: '1px solid #b3d4fc',
                    borderRadius: '8px',
                  }}>
                  <span
                    style={{
                      flex: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.95rem',
                      color: '#1a56db',
                      wordBreak: 'break-all',
                    }}>
                    {survey.short_url}
                  </span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(survey.short_url)
                      setSuccess('Short URL copied to clipboard!')
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#0D9488',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      whiteSpace: 'nowrap',
                    }}>
                    📋 Copy
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p style={{ color: '#666', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
                  Share this URL with respondents to collect survey responses.
                </p>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem 1rem',
                    background: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                  }}>
                  <span
                    style={{
                      flex: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.95rem',
                      color: '#333',
                      wordBreak: 'break-all',
                    }}>
                    {`${window.location.origin}/run/${survey.id}`}
                  </span>
                  <button
                    onClick={() => {
                      const url = `${window.location.origin}/run/${survey.id}`
                      navigator.clipboard.writeText(url)
                      setSuccess('Survey URL copied to clipboard!')
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#0D9488',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      whiteSpace: 'nowrap',
                    }}>
                    📋 Copy
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default SurveySettings
